import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Divider, Flex, Text, Grid, Button
} from 'components/ui';
import CreditCard from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard';
import Radio from 'components/Inputs/Radio/Radio';
import ErrorMsg from 'components/ErrorMsg';
import creditCardUtils from 'utils/CreditCard';
import agentAwareUtils from 'utils/AgentAware';
import EditButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/EditButton';
import RemoveButton from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/RemoveButton';

function isCCDisabled(creditCard, disabled) {
    return creditCard?.isExpired || creditCardUtils.tokenMigrationFailed(creditCard) || disabled;
}

function CreditCardRow({
    creditCard,
    showDivider,
    selectedCreditCardId,
    disabled,
    creditCardIsDefaultPayment,
    showEditRemoveButtons,
    showEditButtonFirst,
    shouldDisplayAddOrDeleteCreditCardButton,
    hideUseThisCardButton,
    locales,
    onSetNewCreditCard,
    onShowEditCreditCardForm,
    onRemoveCreditCard,
    onDeleteCreditCard,
    onUseThisCard
}) {
    const {
        expiredCreditCardMsg, remove, edit, gotIt, useThisCard
    } = locales;

    return (
        <Box data-at={Sephora.debug.dataAt('credit_card_item')}>
            {creditCardUtils.showExpiredMessage(creditCard) && (
                <ErrorMsg
                    lineHeight='none'
                    marginBottom={1}
                    paddingTop={[2, null]}
                    children={expiredCreditCardMsg}
                />
            )}
            <Grid
                columns={['1fr', '1fr auto']}
                paddingTop={4}
                gap={2}
                alignItems={['start', 'start']}
            >
                <Radio
                    paddingY={0}
                    dotOffset={0}
                    alignItems='center'
                    name='payWithCreditCard'
                    disabled={isCCDisabled(creditCard, disabled)}
                    checked={creditCard.creditCardId === selectedCreditCardId}
                    onClick={onSetNewCreditCard(creditCard)}
                    css={styles.radio}
                    aria-disabled={disabled ? 'true' : undefined}
                >
                    <CreditCard
                        creditCard={creditCard}
                        showDefault={creditCardIsDefaultPayment && creditCard.isDefault}
                        isEditView={true}
                    />
                </Radio>
                {showEditRemoveButtons && creditCardUtils.tokenMigrationDisabledOrSucceed(creditCard) && (
                    <Flex
                        alignItems={['center', 'flex-start']}
                        marginLeft={[9, 0]}
                        marginTop={[1, 0]}
                    >
                        {showEditButtonFirst && (
                            <EditButton
                                creditCard={creditCard}
                                editText={edit}
                                showEditButtonFirst={true}
                                editCallback={onShowEditCreditCardForm}
                            />
                        )}
                        {shouldDisplayAddOrDeleteCreditCardButton && (
                            <RemoveButton
                                creditCard={creditCard}
                                removeText={remove}
                                showEditButtonFirst={showEditButtonFirst}
                                removeCallback={onRemoveCreditCard}
                            />
                        )}
                        {!showEditButtonFirst && (
                            <EditButton
                                creditCard={creditCard}
                                editText={edit}
                                editCallback={onShowEditCreditCardForm}
                            />
                        )}
                    </Flex>
                )}
            </Grid>
            {creditCard.creditCardId === selectedCreditCardId && !hideUseThisCardButton && !isCCDisabled(creditCard, disabled) && (
                <Box
                    marginTop={3}
                    marginBottom={4}
                >
                    <Button
                        variant='primary'
                        width={['100%', '100%', '380px']}
                        onClick={onUseThisCard}
                    >
                        {useThisCard}
                    </Button>
                </Box>
            )}
            {creditCardUtils.tokenMigrationFailed(creditCard) && (
                <Grid
                    columns='1fr auto'
                    fontSize='sm'
                    lineHeight='tight'
                    backgroundColor='nearWhite'
                    alignItems='center'
                    padding={3}
                    borderRadius={2}
                    marginTop={10}
                >
                    <Text>{creditCard.message}</Text>
                    {showEditRemoveButtons && (
                        <Button
                            variant='secondary'
                            size='xs'
                            children={gotIt}
                            onClick={onDeleteCreditCard(creditCard)}
                        />
                    )}
                </Grid>
            )}
            {showDivider && <Divider marginTop={Sephora.isAgent && agentAwareUtils.isAgentAwareSession() ? !showEditRemoveButtons && '1em' : 4} />}
        </Box>
    );
}

const styles = {
    radio: {
        '&:focus-visible': {
            outline: '2px solid #0066cc',
            outlineOffset: '2px'
        }
    }
};

export default wrapFunctionalComponent(CreditCardRow, 'CreditCardRow');
